import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import ENDPOINT from '../../../constants/endpoints';
import AsyncSelectPane from '../../../GbsUI/components/AsyncSelectPane';
import Button from '../../../GbsUI/UI/Button';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import ArrowLeftIcon from '../../../GbsUI/UI/icons/ArrowLeft';
import ArrowRightIcon from '../../../GbsUI/UI/icons/ArrowRight';
import SignupLayout from '../components/Layout';
import {SignupContext} from '../context';
import stepFiveSchema from '../schema/step-five';

const SignupStepFour = ({ history }) => {
  const {state, setState} = useContext(SignupContext);
  const [formErrors, setFormErrors] = useState(new Map());
  const initialized = useRef(false);
  const geoPreferencesTemp = useRef(null);

  const validate = useCallback((data, abortEarly = false) => {
    return stepFiveSchema.validate(data, {abortEarly}).then((data) => {
      setFormErrors(new Map());
      return data;
    }).catch((errors) => {
      if (errors.inner.length > 0) {
        const errorsMap = errors.inner.reduce((accum, item) => {
          accum.set(item.path, item.errors);
          return accum;
        }, new Map());

        setFormErrors(errorsMap);
      } else {
        const errorsMap = new Map();
        errorsMap.set(errors.path, errors.errors);

        setFormErrors(errorsMap);
      }

      return false;
    });
  }, []);

  const onSubmit = useCallback(e => {
    e.preventDefault();

    validate(state.location).then((valid) => {
      if (valid) {
        setFormErrors(new Map());
        history.push('/signup/step/5');
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    });

  }, [history, state.location, validate]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    validate(state.location, true);
  }, [state, validate]);

  return (
    <form onSubmit={onSubmit}>
      <SignupLayout
        title={'Step 4: Location Preferences'}
        footer={
          <>
            <Button
              type={'button'}
              variant={'secondary'}
              size={'xl'}
              icon={ArrowLeftIcon}
              iconProps={{size: 21}}
              iconPosition={'left'}
              onClick={() => history.push('/signup/step/3')}
            >
              Back
            </Button>
            <Button
              size={'xl'}
              icon={ArrowRightIcon}
              iconProps={{size: 21}}
              iconPosition={'right'}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className={'signup-form-row'}>
          <div className={'signup-form-item signup-form-item--full'}>
            <AsyncSelectPane
              name={'primaryCity'}
              label={'Current/Primary City'}
              searchHelp={'If your city is not listed, begin typing to search for it.'}
              errors={formErrors.get('primaryCity')}
              title={'Select your Current/Primary City\n'}
              help={'Select your current location (or closest major city)'}
              selected={state?.location?.primaryCity}
              columns={4}
              onChange={v => {
                setState((current) => {
                  return {
                    ...current,
                    location: {
                      ...current.location,
                      primaryCity: v,
                    },
                  };
                });
              }}
              source={ENDPOINT.TAXONOMIES.CITIES}
              notFoundText={'City is not found'}
              searchPlaceholder={'Enter the city name'}
              showIcon={false}
              autoselect
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <AsyncSelectPane
              name={'hometown'}
              label={'Hometown'}
              errors={formErrors.get('hometown')}
              title={'Enter your hometown'}
              searchHelp={'If your hometown is not listed, begin typing to search for it.'}
              help={'Select your hometown or the nearest major city'}
              selected={state?.location?.hometown}
              columns={4}
              onChange={v => {
                setState((current) => {
                  return {
                    ...current,
                    location: {
                      ...current.location,
                      hometown: v,
                    },
                  };
                });
              }}
              source={ENDPOINT.TAXONOMIES.CITIES}
              notFoundText={'City is not found'}
              searchPlaceholder={'Enter your hometown'}
              showIcon={false}
              autoselect
            />
          </div>
          <div className={'signup-form-item signup-form-item--full'}>
            <AsyncSelectPane
              name={'geoPreferences'}
              label={'Preferred Work Locations'}
              errors={formErrors.get('geoPreferences')}
              title={'Where you want to Work/Live'}
              searchHelp={'If your preferred city is not listed, begin typing to search for it.'}
              help={'Select top cities where you would like to work/live'}
              selected={state?.location?.geoPreferences || []}
              onOpen={() => {
                geoPreferencesTemp.current = {
                  highlyMobile: !!state?.location?.highlyMobile,
                  sendJobs: !!state?.location?.sendJobs,
                };
              }}
              limit={10}
              columns={4}
              onCancel={() => {
                setState((current) => {
                  return {
                    ...current,
                    location: {
                      ...current.location,
                      highlyMobile: geoPreferencesTemp.current.highlyMobile,
                      sendJobs: geoPreferencesTemp.current.sendJobs,
                    },
                  };
                });
              }}
              beforeList={(v) => {
                return (
                  <div className={'geo-pref-options'}>
                    <Checkbox
                      checked={!!state?.location?.highlyMobile}
                      label={'Highly mobile – open to additional locations'}
                      onChange={(e) => {
                        const highlyMobile = e.target.checked;

                        setState((prevState) => ({
                          ...prevState,
                          location: {
                            ...prevState.location,
                            highlyMobile,
                            sendJobs: highlyMobile ? true : prevState.location.sendJobs,
                          }
                        }));
                      }}
                    />
                    <Checkbox
                      checked={!!state?.location?.sendJobs}
                      label={'Send me jobs (for my own visibility / knowledge) for all locations'}
                      onChange={(e) => {
                        const sendJobs = e.target.checked;

                        setState((prevState) => ({
                          ...prevState,
                          location: {
                            ...prevState.location,
                            sendJobs,
                          }
                        }));
                      }}
                    />
                    {v.length >= 3 && v.length <= 5 && (
                      <p className={'geo-pref-options__note'}>
                        Suggestion:  Keep your key cities to a short list. If interested in many cities, check the “highly mobile” box.
                      </p>
                    )}
                    {v.length >= 6 && v.length <= 9 && (
                      <p className={'geo-pref-options__note'}>
                        Warning:  Recommend selecting fewer than 5 cities.  If interested in many cities, check the “highly mobile” box
                      </p>
                    )}
                    {v.length >= 10 && (
                      <p className={'geo-pref-options__note'}>
                        Maximum limit of cities is 10. If interested in many cities, check the “highly mobile” box.
                      </p>
                    )}
                  </div>
                );
              }}
              onChange={v => {
                setState((current) => {
                  return {
                    ...current,
                    location: {
                      ...current.location,
                      geoPreferences: v,
                    },
                  };
                });
              }}
              source={ENDPOINT.TAXONOMIES.CITIES}
              notFoundText={'City is not found'}
              searchPlaceholder={'Enter city name'}
              showIcon={false}
              multiple
            />
          </div>
        </div>
      </SignupLayout>
    </form>
  );
};

export default SignupStepFour;

//
